import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Stack,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconDownload,
  IconPrinter,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";

import { tcTechnicalSummaryService } from "../../services/tcTechnicalSummary.service";
import {
  getResponseDownloadFilename,
  triggerBlobDownload,
} from "../../utils/downloadFilename";

interface Props {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  caseNumber?: string;
  originalReport?: boolean;
}

export function TcTechnicalSummaryViewerModal({
  opened,
  onClose,
  caseId,
  caseNumber,
  originalReport = false,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const filenameRef = useRef("technical-summary.pdf");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const reportTitle = useMemo(
    () => (originalReport ? "Original Technical Summary" : "Technical Summary"),
    [originalReport]
  );

  useEffect(() => {
    if (!opened || !caseId) return undefined;
    let active = true;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);
    setBlobUrl(null);
    tcTechnicalSummaryService
      .getPdf(caseId, "inline")
      .then((response) => {
        if (!active) return;
        const blob = new Blob([response.data as BlobPart], {
          type: "application/pdf",
        });
        blobRef.current = blob;
        filenameRef.current = getResponseDownloadFilename(
          response.headers["content-disposition"],
          "technical-summary.pdf"
        );
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch((caughtError) => {
        if (!active) return;
        const conflict =
          axios.isAxiosError(caughtError) &&
          caughtError.response?.status === 409;
        setError(
          conflict
            ? "The Technical Summary is not ready because the TC case data is incomplete or changed. Refresh the case and try again."
            : "The Technical Summary could not be loaded. Please try again."
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      blobRef.current = null;
    };
  }, [caseId, opened]);

  const handleDownload = useCallback(() => {
    if (blobRef.current)
      triggerBlobDownload(blobRef.current, filenameRef.current);
  }, []);

  const handlePrint = useCallback(() => {
    const contentWindow = iframeRef.current?.contentWindow;
    if (!contentWindow) {
      notifications.show({
        color: "red",
        title: "Print failed",
        message: "The Technical Summary is not ready to print.",
      });
      return;
    }
    contentWindow.focus();
    contentWindow.print();
  }, []);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`${reportTitle}${caseNumber ? ` - ${caseNumber}` : ""}`}
      size="90%"
      centered
      styles={{
        body: { padding: 0 },
        content: { display: "flex", flexDirection: "column" },
      }}
    >
      <Stack gap={0} style={{ height: "80vh" }}>
        {originalReport && (
          <Alert color="blue" m="sm">
            This is the preserved original NovoPath technical report. It has not
            been regenerated.
          </Alert>
        )}
        <Box style={{ flex: 1, minHeight: 0, position: "relative" }}>
          {loading && (
            <Center h="100%">
              <Loader size="md" />
            </Center>
          )}
          {error && !loading && (
            <Box p="md">
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Could not load Technical Summary"
                color="red"
                role="alert"
              >
                {error}
              </Alert>
            </Box>
          )}
          {blobUrl && !loading && !error && (
            <iframe
              ref={iframeRef}
              src={`${blobUrl}#toolbar=0&navpanes=0&zoom=FitW`}
              title={
                originalReport
                  ? "Original TC Technical Summary PDF"
                  : "TC Technical Summary PDF"
              }
              style={{ width: "100%", height: "100%", border: 0 }}
            />
          )}
        </Box>
        <Group
          justify="space-between"
          gap="xs"
          p="sm"
          style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
        >
          <Button variant="subtle" onClick={onClose}>
            Close
          </Button>
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              onClick={handlePrint}
              disabled={!blobUrl}
            >
              Print
            </Button>
            <Button
              leftSection={<IconDownload size={16} />}
              onClick={handleDownload}
              disabled={!blobUrl}
            >
              Download
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
